import { Spinner } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiExternalLink, FiFileText } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { getScorLocale, scorT } from 'i18n/scorLocale';
import AppRoute from 'router/AppRoute';
import {
  useFetchScorMyInvoicesQuery,
  useFetchScorMyPurchasesQuery,
} from 'rtkQuery/sharespro/queries/scor';
import { formatScorInvoiceMoney } from './scorInvoice';
import {
  buildScorInvoiceRows,
  getScorInvoiceListStatus,
} from './scorInvoiceList';
import styles from './scorInvoices.module.scss';
import scorStyles from './scor.module.scss';

const dateText = (value?: string | null): string => {
  const date = new Date(value ?? '');

  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(getScorLocale(), { dateStyle: 'medium' });
};

export default function ScorInvoicesPage(): JSX.Element {
  useTranslation();
  const invoices = useFetchScorMyInvoicesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const purchases = useFetchScorMyPurchasesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [visibleCount, setVisibleCount] = useState(10);
  const loadMoreRef = useRef<HTMLButtonElement>(null);
  const rows = buildScorInvoiceRows(
    invoices.data?.data?.items,
    purchases.data?.data?.items
  );
  const loading = invoices.isLoading || purchases.isLoading;
  const failed = invoices.isError || purchases.isError;
  const hasMore = visibleCount < rows.length;

  useEffect(() => {
    const target = loadMoreRef.current;
    if (
      !target ||
      !hasMore ||
      loading ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }
    let active = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (active && entries.some((entry) => entry.isIntersecting)) {
          active = false;
          setVisibleCount((count) => count + 10);
        }
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(target);

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [hasMore, loading, visibleCount, rows.length]);

  const statusLabels = {
    paid: 'invoiceListPaid',
    overdue: 'overdue',
    waiting: 'invoiceListWaiting',
    closed: 'invoiceListCancelled',
    refunded: 'invoiceListRefunded',
  } as const;

  return (
    <div className={styles.page}>
      <header className={scorStyles.pageHeader}>
        <div>
          <div className={scorStyles.eyebrow}>SharesPro SCOR</div>
          <h1 className={scorStyles.pageTitle}>{scorT('invoicesTitle')}</h1>
          <div className={scorStyles.muted}>{scorT('invoicesDescription')}</div>
        </div>
      </header>
      <section className={styles.card} aria-label={scorT('invoicesTitle')}>
        {failed && (
          <div role="alert" className={styles.notice}>
            {scorT('invoiceListIncomplete')}{' '}
            <button
              type="button"
              onClick={() => {
                if (invoices.isError) void invoices.refetch();
                if (purchases.isError) void purchases.refetch();
              }}
            >
              {scorT('retry')}
            </button>
          </div>
        )}
        {loading && (
          <div className={styles.state} role="status">
            <Spinner />
            <span>{scorT('loading')}</span>
          </div>
        )}
        {!loading && rows.length === 0 && (
          <p className={styles.state}>
            {failed
              ? scorT('unableToLoadDataTryRefreshingThe')
              : scorT('noInvoicesYet')}
          </p>
        )}
        {rows.length > 0 && (
          <>
            <div
              className={styles.tableScroll}
              tabIndex={0}
              role="region"
              aria-label={scorT('invoiceTable')}
            >
              <table className={styles.table}>
                <thead>
                  <tr>
                    {(
                      [
                        'invoiceColumn',
                        'payment',
                        'invoiceAmountColumn',
                        'status',
                        'invoiceDeadlineColumn',
                        'invoiceActionColumn',
                      ] as const
                    ).map((key) => (
                      <th scope="col" key={key}>
                        {scorT(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, visibleCount).map((row) => {
                    const status = getScorInvoiceListStatus(row);

                    return (
                      <tr key={row.rowKey}>
                        <td>
                          <div className={styles.number}>
                            <FiFileText aria-hidden="true" />
                            <div>
                              <strong>
                                {row.number ??
                                  scorT('invoice', { v0: row.id ?? '—' })}
                              </strong>
                              <small>
                                {row.paidAt
                                  ? scorT('purchasePaidAt', {
                                      v0: dateText(row.paidAt),
                                    })
                                  : dateText(row.issued_at)}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`${styles.badge} ${
                              row.kind === 1
                                ? styles.subscription
                                : [2, 3, 4].includes(row.kind ?? 0)
                                ? styles.report
                                : styles.waiting
                            }`}
                          >
                            {row.kind_label ?? scorT('scorInvoice')}
                          </span>
                        </td>
                        <td className={styles.amount}>
                          {formatScorInvoiceMoney(row.amount_minor)}
                        </td>
                        <td>
                          <span
                            className={`${styles.badge} ${
                              status === 'paid'
                                ? styles.paid
                                : status === 'overdue'
                                ? styles.overdue
                                : styles.waiting
                            }`}
                          >
                            {row.status_label ?? scorT(statusLabels[status])}
                          </span>
                        </td>
                        <td className={styles.deadline}>
                          {dateText(row.valid_until)}
                        </td>
                        <td>
                          {row.id != null ? (
                            <Link
                              className={styles.open}
                              to={AppRoute.scorInvoice(row.id)}
                            >
                              <FiExternalLink aria-hidden="true" />
                              {scorT('openInvoice')}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <footer className={styles.footer}>
              <span>
                {scorT('invoiceListRange', {
                  from: 1,
                  to: Math.min(visibleCount, rows.length),
                  total: rows.length,
                })}
              </span>
              {hasMore && (
                <button
                  ref={loadMoreRef}
                  type="button"
                  className={styles.loadMore}
                  disabled={loading}
                  onClick={() => setVisibleCount((count) => count + 10)}
                >
                  {scorT('loadMoreInvoices')}
                </button>
              )}
            </footer>
          </>
        )}
      </section>
    </div>
  );
}
